import { useRef, useState, type PointerEvent } from "react";
import { fmt, type Pt, type WallInput } from "@tasduvar/engine";
import { toSvg, type Box, type Transform } from "./geometry";
import { snap } from "./snap";

/** Ekran pikseli → SVG birimi çarpanı (PolygonEditor ile aynı) */
function pxScale(el: Element): number {
  const svg = el.closest("svg");
  const ctm = svg && "getScreenCTM" in svg ? (svg as SVGSVGElement).getScreenCTM?.() : null;
  return ctm && ctm.a ? 1 / ctm.a : 1;
}

/**
 * Zemin çizgisi tutamaçları: ön zemin çizgisi düşey sürüklenince gömme derinliği Df, dolgu yüzeyinin uzak ucu sürüklenince dolgu eğimi β
 * değişir — kesit "çizilirken" bu iki parametre de çizimden okunur. Df ızgaraya (5 cm), β yarım dereceye yuvarlanır.
 */
export function GroundHandles({ t, wall, box, surf, yTop, onDf, onBeta }: { t: Transform; wall: WallInput; box: Box; surf: Pt[]; yTop: number; onDf: (v: number) => void; onBeta: (deg: number) => void }): JSX.Element {
  const drag = useRef<{ kind: "Df" | "beta"; clientY: number; start: number; k: number } | null>(null);
  const [hot, setHot] = useState<"Df" | "beta" | null>(null);
  const Df = wall.geometry.embedmentDepth, beta = wall.backfill.slopeAngle;
  const gL = toSvg(t, { x: box.minX, y: Df }), gR = toSvg(t, { x: 0, y: Df });
  const top = surf[1] ?? surf[0]!, far = surf[surf.length - 1]!;
  const sTop = toSvg(t, top), sFar = toSvg(t, far);
  const dy = (e: { clientY: number }, d: NonNullable<typeof drag.current>): number => -((e.clientY - d.clientY) * d.k) / t.scale;
  const down = (kind: "Df" | "beta") => (e: PointerEvent<SVGElement>): void => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { kind, clientY: e.clientY, start: kind === "Df" ? Df : far.y, k: pxScale(e.currentTarget) };
  };
  const move = (e: PointerEvent<SVGElement>): void => {
    const d = drag.current; if (!d) return;
    if (d.kind === "Df") onDf(Math.min(Math.max(0, snap(d.start + dy(e, d))), Math.max(0, yTop - 0.05)));
    else { const y = d.start + dy(e, d); const deg = (Math.atan2(y - top.y, far.x - top.x) * 180) / Math.PI; onBeta(Math.min(60, Math.max(0, Math.round(deg * 2) / 2))); }
  };
  const up = (): void => { drag.current = null; };
  const common = { onPointerMove: move, onPointerUp: up, onPointerCancel: up, style: { cursor: "ns-resize", touchAction: "none" } as const };
  return (
    <g data-part="ground-handles">
      <line data-handle="Df" x1={gL.x} y1={gL.y} x2={gR.x} y2={gR.y} stroke={hot === "Df" ? "#1D7A46" : "transparent"} strokeWidth={hot === "Df" ? 3 : 10} strokeOpacity={0.6}
        onPointerDown={down("Df")} onPointerEnter={() => setHot("Df")} onPointerLeave={() => setHot(null)} {...common} />
      <rect x={(gL.x + gR.x) / 2 - 5} y={gL.y - 5} width={10} height={10} fill="#fff" stroke="#1D7A46" strokeWidth={1.5} pointerEvents="none" />
      <line data-handle="beta" x1={sTop.x} y1={sTop.y} x2={sFar.x} y2={sFar.y} stroke={hot === "beta" ? "#1D7A46" : "transparent"} strokeWidth={hot === "beta" ? 3 : 10} strokeOpacity={0.6}
        onPointerDown={down("beta")} onPointerEnter={() => setHot("beta")} onPointerLeave={() => setHot(null)} {...common} />
      <circle cx={sFar.x} cy={sFar.y} r={5} fill="#fff" stroke="#1D7A46" strokeWidth={1.5} pointerEvents="none" />
      <text x={sFar.x - 6} y={sFar.y - 8} textAnchor="end" className="text-[10px] num" fill="#1D7A46" pointerEvents="none">β = {fmt(beta, 1)}°</text>
    </g>
  );
}
